'use client'

import FloatingParticlesBackground from '@/app/[locale]/(create)/partner/components/FloatingParticlesBackground'
import { Link } from '@/i18n/navigation'
import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'

interface SupportedFormat {
  key: string
  titleKey: string
  subtitleKey: string
  href?: string
}

const SUPPORTED_FORMATS: SupportedFormat[] = [
  {
    key: 'videoMp4',
    titleKey: 'Video (MP4)',
    subtitleKey: 'H.264 codec',
  },
  {
    key: 'unityWebGL',
    titleKey: 'Unity WebGL',
    subtitleKey: 'Web build',
    href: 'https://docs.viverse.com/standalone-publishing/unitywebgl-examples',
  },
  {
    key: 'Godot',
    titleKey: 'Godot',
    subtitleKey: 'Web build',
    href: 'https://docs.viverse.com/standalone-publishing/godot-html5-example',
  },
  {
    key: 'ThreeJs',
    titleKey: 'Three.js',
    subtitleKey: 'Web build',
    href: 'https://docs.viverse.com/standalone-publishing/three.js-with-vite-or-getting-started-tutorial',
  },
  {
    key: 'embedContent',
    titleKey: 'Embed Content',
    subtitleKey: 'HTML iFrame',
    href: 'https://docs.viverse.com/standalone-publishing/wonderland-engine',
  },
  {
    key: 'webglCanvas',
    titleKey: 'WebGL Canvas',
    subtitleKey: 'Web build',
  },
  {
    key: 'openBrush',
    titleKey: 'Open Brush',
    subtitleKey: 'VR painting',
    href: 'https://docs.viverse.com/standalone-publishing/open-brush',
  },
  {
    key: 'reactApps',
    titleKey: 'React Apps',
    subtitleKey: 'NPM build',
    href: 'https://docs.viverse.com/standalone-publishing/publishing-to-viverse-with-the-cli',
  },
]

export default function NoCodeSection() {
  const t = useTranslations()

  return (
    <section className="relative overflow-hidden bg-linear-to-b from-[#131b26] to-[#121216] px-4 py-12 md:px-8 md:py-20 lg:px-[72px]">
      <FloatingParticlesBackground />
      <div className="relative mx-auto flex max-w-full flex-col items-center gap-8 md:max-w-[1296px] md:py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="flex w-full flex-col items-center gap-2 md:gap-4"
        >
          <h2 className="text-center text-[28px] leading-[30px] font-extrabold tracking-[0.15px] text-white md:text-[38px] md:leading-10">
            {t('partner.no-code.title')}
          </h2>
          <p className="text-vive-primary-20 max-w-[720px] text-center leading-[26px] tracking-[0.25px] md:text-lg md:leading-8">
            {t('partner.no-code.description')}
          </p>
        </motion.div>

        <div className="grid w-full max-w-[960px] grid-cols-2 gap-4 md:grid-cols-4 lg:gap-6">
          {SUPPORTED_FORMATS.map((format, index) => {
            const content = (
              <motion.div
                className={cn(
                  'flex flex-col gap-2 rounded-2xl border border-[rgba(181,194,213,0.1)] bg-[rgba(42,54,73,0.3)] px-5 py-5 transition-colors',
                  format.href ? 'cursor-pointer hover:bg-[rgba(42,54,73,0.4)]' : '',
                )}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, ease: 'easeOut', delay: index * 0.1 }}
              >
                <h3 className="text-[16px] leading-6 font-semibold text-white">
                  {format.titleKey}
                </h3>
                <p className="text-vive-primary-30 text-[12px] leading-4 font-normal">
                  {format.subtitleKey}
                </p>
              </motion.div>
            )

            return format.href ? (
              <Link key={format.key} href={format.href} target="_blank" className="w-full">
                {content}
              </Link>
            ) : (
              <div key={format.key} className="w-full">
                {content}
              </div>
            )
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="flex flex-wrap items-center justify-center gap-1 text-center"
        >
          <span className="text-vive-primary-30 text-center text-xs leading-[18px] tracking-[0.2px] md:text-[14px] md:leading-[22px]">
            {t('partner.no-code.footer.question')}
          </span>
          <div className="text-vive-primary-30 text-center">
            <Link
              href="https://docs.viverse.com/"
              target="_blank"
              className="text-vive-brand-50 hover:text-vive-brand-40 active:text-vive-brand-60 inline-flex w-fit items-center gap-2 text-xs tracking-[0.2px] underline transition-all md:text-[14px]"
            >
              {t('partner.no-code.footer.docs-link')}
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
